import {
  type ReactNode,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Box,
  Pagination,
  PaginationItem,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';

import { ConditionalLinkWrapper } from '@modules/_shared/components/ConditionalLinkWrapper';
import type { RaLinkProps } from '@modules/_shared/components/RaLink';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { useLocale } from '@shared/hooks/locale';
import type { SetHook } from '@shared/hooks/useSet';

const PaginationPreviousIcon = () => <ArrowLeftIcon size={20} />;
const PaginationNextIcon = () => <ArrowRightIcon size={20} />;

export type GridItem = { id: string | number; [key: string]: any };

type RaGridBaseProps<T extends GridItem> = {
  items?: T[];
  loading?: boolean;
  loadingCount?: boolean;
  totalCount?: number;
  pageSize?: number;
  emptyMessage?: string;
  queryParamsScope?: string;
  renderSkeleton: () => ReactNode;
  renderItemCard: (item: T) => ReactNode;
  getItemLink?: (id: string | number) => {
    pathname: string;
    search: string;
  };
  selection?: SetHook<string | number>;
  gridItemProps?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
};

export type RaGridProps<T extends GridItem> = RaGridBaseProps<T>;

// Minimum readable card width: below this, prices and addresses truncate
// into an unusable card, so the column count must drop instead
const MIN_CARD_WIDTH = 260;

const RaGrid = <T extends GridItem>({
  items = [],
  loading = false,
  loadingCount = false,
  totalCount = 0,
  pageSize = 24,
  emptyMessage,
  renderSkeleton,
  renderItemCard,
  getItemLink,
  selection,
  gridItemProps,
  queryParamsScope,
}: RaGridProps<T>) => {
  const { t } = useLocale();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isEmpty = !loading && items.length === 0;
  const [searchParams, setSearchParams] =
    useScopedSearchParams(queryParamsScope);
  const searchParamName = 'offset'; // Hardcoded to always use 'offset'
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Calculate page from URL params
  const calculatePageFromUrl = useCallback(() => {
    const offset = parseInt(searchParams.get(searchParamName) ?? '0');
    return Math.floor(offset / pageSize) + 1;
  }, [searchParams, pageSize, searchParamName]);

  const [currentPage, setCurrentPage] = useState(calculatePageFromUrl());

  // Update page when URL changes
  useEffect(() => {
    setCurrentPage(calculatePageFromUrl());
  }, [searchParams, pageSize, calculatePageFromUrl]);

  const handlePageChange = (page: number) => {
    searchParams.set(searchParamName, ((page - 1) * pageSize).toString());
    setSearchParams(searchParams);
    setCurrentPage(page);

    // Scroll the container to top instead of the window
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  // Columns derive from the actual container width (sidebar and side
  // panels included), never letting a card shrink below MIN_CARD_WIDTH.
  // Explicit gridItemProps keep the legacy viewport-based 12-column
  // behaviour (e.g. { xs: 12 } for single-column lists).
  const gridTemplateColumns = gridItemProps
    ? Object.fromEntries(
        Object.entries(gridItemProps).map(([breakpoint, columns]) => [
          breakpoint,
          `repeat(${Math.max(1, Math.floor(12 / columns))}, minmax(0, 1fr))`,
        ]),
      )
    : `repeat(auto-fill, minmax(min(${MIN_CARD_WIDTH}px, 100%), 1fr))`;

  return (
    <Box
      sx={{
        flexGrow: 1,
        position: 'relative',
        zIndex: 0,
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <Box
          ref={scrollContainerRef}
          sx={{
            overflow: 'auto',
            height: '100%',
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ minHeight: '100%' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns,
                gap: { xs: 1, sm: 2 },
                p: { xs: 1, sm: 2 },
              }}
            >
              {loading ? (
                Array.from({ length: pageSize }).map((_, index) => (
                  <Box key={`skeleton-${index}`} sx={{ minWidth: 0 }}>
                    {renderSkeleton()}
                  </Box>
                ))
              ) : isEmpty ? (
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="body1" align="center">
                    {emptyMessage || t('No results found')}
                  </Typography>
                </Box>
              ) : (
                items.map(item => {
                  const itemId = item.id;
                  const isSelected = selection?.has(itemId);
                  const content = renderItemCard(item);

                  // Modified approach: let the card handle its own selection
                  // We just pass isSelected through, but don't manage click events

                  // Wrap with Link only if getItemLink is provided
                  const linkProps: RaLinkProps | undefined = getItemLink
                    ? {
                        to: getItemLink(itemId),
                        preserveSearchParams: true,
                        style: {
                          textDecoration: 'none',
                          height: '100%',
                          display: 'block',
                        },
                      }
                    : undefined;

                  return (
                    <Box
                      key={`item-${itemId}`}
                      sx={{
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        ...(isSelected && {
                          '& > *': {
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: '2px',
                          },
                        }),
                      }}
                    >
                      <ConditionalLinkWrapper link={linkProps}>
                        {content}
                      </ConditionalLinkWrapper>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
          {totalCount > 0 || loadingCount ? (
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: ['center', 'flex-start'],
                px: 2,
                // 52px total with the border inside, mirroring the data
                // grids' 52px pagination footer and the sidebar's account
                // row so the separator lines align across the app
                height: 52,
                backgroundColor: 'background.paper',
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {loadingCount ? (
                <Skeleton
                  variant="rectangular"
                  width={300}
                  height={36}
                  sx={{ borderRadius: 1 }}
                />
              ) : (
                <Pagination
                  count={Math.ceil(totalCount / pageSize)}
                  color="primary"
                  shape="rounded"
                  siblingCount={isMobile ? 0 : 1}
                  page={currentPage}
                  sx={{ justifyContent: 'center' }}
                  onChange={(_e, page) => handlePageChange(page)}
                  renderItem={item => (
                    <PaginationItem
                      slots={{
                        next: PaginationNextIcon,
                        previous: PaginationPreviousIcon,
                      }}
                      {...item}
                    />
                  )}
                />
              )}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default memo(RaGrid) as unknown as <T extends GridItem>(
  props: RaGridProps<T>,
) => JSX.Element;
